import React from 'react';
import { IoClose } from 'react-icons/io5';
import { format, parseISO } from 'date-fns'; // Import date-fns for formatting

const ActiveFilters = ({ filters, onRemoveFilter, onRemoveAll }) => {
  // Check for active yacht filters, comparing against defaults
  const hasActiveFilters = 
    filters.cruiseType ||
    filters.destination ||
    filters.bookingDate ||
    filters.numberOfGuests ||
    (filters.yachtType && filters.yachtType.length > 0) || 
    filters.cabins ||
    (filters.priceRange && (filters.priceRange[0] !== 0 || filters.priceRange[1] !== 5000)) || // Default yacht price range
    (filters.lengthRange && (filters.lengthRange[0] !== 10 || filters.lengthRange[1] !== 50)); // Default yacht length range

  if (!hasActiveFilters) return null;

  // Helper to format cruise type label
  const formatCruiseType = (type) => {
      switch (type) {
          case 'half-day-morning': return 'Half-Day Morning';
          case 'half-day-sunset': return 'Half-Day Sunset';
          case 'full-day': return 'Full-Day';
          default: return type; // Return original if not matched
      }
  };

  // Helper to format destination label
  const formatDestination = (dest) => {
      switch (dest) {
          case 'south-coast': return 'South Coast';
          case 'delos-rhenia': return 'Delos & Rhenia';
          case 'south-delos-rhenia': return 'South Coast & Delos/Rhenia';
          default: return dest; // Return original if not matched
      }
  };

  // Helper to format date safely
  const formatDate = (dateInput) => {
      try {
          // Handle both string and Date object inputs
          if (dateInput instanceof Date) {
              return format(dateInput, 'dd/MM/yyyy');
          } else if (typeof dateInput === 'string') {
              return format(parseISO(dateInput), 'dd/MM/yyyy');
          }
          return "Invalid Date";
      } catch (e) {
          console.error("Error formatting date:", dateInput, e);
          return "Invalid Date";
      }
  }

  return (
    <div className="mb-6 JosefinSans">
      <h3 className="text-sm font-semibold text-gray-600 mb-3">Active Filters:</h3>
      <div className="flex flex-wrap gap-2 mb-3">
        {/* Display Booking Date & Time */}
        {filters.bookingDate && (
          <div className="flex items-center bg-blue-100 text-blue-800 shadow-sm rounded-full px-3 py-1 text-xs font-medium">
            <span>
              Date: {formatDate(filters.bookingDate)} {filters.bookingTime}
            </span>
            <button
              onClick={() => onRemoveFilter('bookingDate')} // Use correct filterType
              className="ml-1.5 text-blue-600 hover:text-red-500 transition-colors"
              aria-label="Remove date filter"
            >
              <IoClose size={14} />
            </button>
          </div>
        )}

        {/* Display Cruise Type */}
        {filters.cruiseType && (
          <div className="flex items-center bg-blue-100 text-blue-800 shadow-sm rounded-full px-3 py-1 text-xs font-medium">
            <span>Cruise: {formatCruiseType(filters.cruiseType)}</span>
            <button
              onClick={() => onRemoveFilter('cruiseType')}
              className="ml-1.5 text-blue-600 hover:text-red-500 transition-colors"
              aria-label="Remove cruise type filter"
            >
              <IoClose size={14} />
            </button>
          </div>
        )}

        {/* Display Destination */}
        {filters.destination && (
          <div className="flex items-center bg-blue-100 text-blue-800 shadow-sm rounded-full px-3 py-1 text-xs font-medium">
            <span>Destination: {formatDestination(filters.destination)}</span>
            <button
              onClick={() => onRemoveFilter('destination')}
              className="ml-1.5 text-blue-600 hover:text-red-500 transition-colors"
              aria-label="Remove destination filter"
            >
              <IoClose size={14} />
            </button>
          </div>
        )}

        {/* Display Number of Guests */}
        {filters.numberOfGuests && (
          <div className="flex items-center bg-blue-100 text-blue-800 shadow-sm rounded-full px-3 py-1 text-xs font-medium">
            <span>Guests: {filters.numberOfGuests}</span>
            <button
              onClick={() => onRemoveFilter('numberOfGuests')}
              className="ml-1.5 text-blue-600 hover:text-red-500 transition-colors"
              aria-label="Remove guests filter"
            >
              <IoClose size={14} />
            </button>
          </div>
        )}
        
        {/* Display Yacht Types */}
        {filters.yachtType?.map(type => (
          <div key={type} className="flex items-center bg-blue-100 text-blue-800 shadow-sm rounded-full px-3 py-1 text-xs font-medium">
            <span>Type: {type}</span>
            <button
              onClick={() => onRemoveFilter('yachtType', type)} // Pass value to remove
              className="ml-1.5 text-blue-600 hover:text-red-500 transition-colors"
              aria-label={`Remove ${type} filter`}
            >
              <IoClose size={14} />
            </button>
          </div>
        ))}

        {/* Display Cabins */}
        {filters.cabins && (
          <div className="flex items-center bg-blue-100 text-blue-800 shadow-sm rounded-full px-3 py-1 text-xs font-medium">
            <span>Cabins: {filters.cabins}</span>
            <button
              onClick={() => onRemoveFilter('cabins')}
              className="ml-1.5 text-blue-600 hover:text-red-500 transition-colors"
              aria-label="Remove cabins filter"
            >
              <IoClose size={14} />
            </button>
          </div>
        )}

        {/* Display Price Range */}
        {(filters.priceRange && (filters.priceRange[0] !== 500 || filters.priceRange[1] !== 5000)) && (
          <div className="flex items-center bg-blue-100 text-blue-800 shadow-sm rounded-full px-3 py-1 text-xs font-medium">
            <span>Price: €{filters.priceRange[0]} - €{filters.priceRange[1]}</span>
            <button
              onClick={() => onRemoveFilter('priceRange')}
              className="ml-1.5 text-blue-600 hover:text-red-500 transition-colors"
              aria-label="Remove price range filter"
            >
              <IoClose size={14} />
            </button>
          </div>
        )}

         {/* Display Length Range */}
        {(filters.lengthRange && (filters.lengthRange[0] !== 10 || filters.lengthRange[1] !== 50)) && (
          <div className="flex items-center bg-blue-100 text-blue-800 shadow-sm rounded-full px-3 py-1 text-xs font-medium">
            <span>Length: {filters.lengthRange[0]}m - {filters.lengthRange[1]}m</span>
            <button
              onClick={() => onRemoveFilter('lengthRange')}
              className="ml-1.5 text-blue-600 hover:text-red-500 transition-colors"
              aria-label="Remove length range filter"
            >
              <IoClose size={14} />
            </button>
          </div>
        )}
      </div>
      
      <button
        onClick={onRemoveAll}
        className="text-red-600 hover:text-red-700 text-xs font-medium transition-colors"
      >
        Remove all filters
      </button>
    </div>
  );
};

export default ActiveFilters;